/**
 * TemplateCard - Display card for global templates
 *
 * Shows template information with:
 * - Category and visibility badges
 * - Template name and content preview
 * - Usage stats
 * - Quick actions
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  MoreVertical,
  Copy,
  Pencil,
  Trash2,
  Eye,
  Users,
} from 'lucide-react';
import {
  Card,
  CardContent,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from '@respectly/ui';
import type { GlobalTemplate } from '@respectly/shared-types';
import { TemplateCategoryBadge, getCategoryColor } from './TemplateCategoryBadge';
import { TemplateVisibilityBadge } from './TemplateVisibilityBadge';

// ============================================
// Types
// ============================================

interface TemplateCardProps {
  template: GlobalTemplate;
  onEdit?: (template: GlobalTemplate) => void;
  onDuplicate?: (template: GlobalTemplate) => void;
  onDelete?: (template: GlobalTemplate) => void;
  onPreview?: (template: GlobalTemplate) => void;
  onVisibilityChange?: (template: GlobalTemplate) => void;
  className?: string;
}

// ============================================
// Component
// ============================================

export function TemplateCard({
  template,
  onEdit,
  onDuplicate,
  onDelete,
  onPreview,
  onVisibilityChange,
  className,
}: TemplateCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Get gradient color based on type
  const gradientColor = getCategoryColor(template.type);

  // Truncate content for preview
  const contentPreview = template.content.length > 120
    ? template.content.substring(0, 120) + '...'
    : template.content;

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Card
        className={cn(
          'relative overflow-hidden transition-all duration-200',
          isHovered && 'shadow-lg border-slate-300 dark:border-slate-600',
          !template.isActive && 'opacity-60',
          className
        )}
      >
        {/* Gradient accent bar */}
        <div
          className="absolute top-0 left-0 right-0 h-1"
          style={{ backgroundColor: gradientColor }}
        />

        <CardContent className="p-4 pt-5">
          {/* Header with badges and actions */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex flex-wrap items-center gap-2">
              <TemplateCategoryBadge category={template.type} size="sm" />
              <TemplateVisibilityBadge visibility={template.visibility} size="sm" />
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                >
                  <MoreVertical className="h-4 w-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {onPreview && (
                  <DropdownMenuItem onClick={() => onPreview(template)}>
                    <Eye className="mr-2 h-4 w-4" />
                    Preview
                  </DropdownMenuItem>
                )}
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(template)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                {onDuplicate && (
                  <DropdownMenuItem onClick={() => onDuplicate(template)}>
                    <Copy className="mr-2 h-4 w-4" />
                    Duplicate
                  </DropdownMenuItem>
                )}
                {onVisibilityChange && (
                  <DropdownMenuItem onClick={() => onVisibilityChange(template)}>
                    <Users className="mr-2 h-4 w-4" />
                    Change Visibility
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete(template)}
                      className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Template name */}
          <h3 className="font-semibold text-slate-900 dark:text-white mb-2 line-clamp-1">
            {template.name}
          </h3>

          {/* Content preview */}
          <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-3 mb-4 min-h-[3.75rem]">
            {contentPreview}
          </p>

          {/* Footer stats */}
          <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
              <Users className="h-4 w-4" />
              <span className="tabular-nums">{template.usageCount}</span>
              <span className="text-slate-400 dark:text-slate-500">uses</span>
            </div>

            {!template.isActive && (
              <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                Inactive
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Skeleton Loader
// ============================================

export function TemplateCardSkeleton() {
  return (
    <Card className="relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 bg-slate-200 dark:bg-slate-700 animate-pulse" />
      <CardContent className="p-4 pt-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex gap-2">
            <div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" />
            <div className="h-5 w-20 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse" />
          </div>
          <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </div>
        <div className="h-5 w-3/4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-2" />
        <div className="space-y-2 mb-4">
          <div className="h-4 w-full bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          <div className="h-4 w-5/6 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          <div className="h-4 w-2/3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </div>
        <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
          <div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
